import React, { useEffect, forwardRef, useState, useContext } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import Select from 'react-select';
import { Context } from '../Context/Context';
import { motion, AnimatePresence } from "framer-motion";
import { FaRegImage } from "react-icons/fa6";
import imageCompression from "browser-image-compression";

const CustomInput = forwardRef(({ value, onClick, placeholder }, ref) => (
    <input
        type="text"
        className="border-[1.2px] mt-1 border-black/20 rounded-md w-full py-[10px] px-[14px] outline-none"
        onClick={onClick}
        value={value}
        ref={ref}
        placeholder={placeholder}
        readOnly
    />
));

const VerifyIdentity = () => {
    const location = useLocation()
    const navigate = useNavigate();
    const [idType, setIdType] = useState('')
    const [passport, setPassport] = useState(null)
    const [frontDoc, setFrontDoc] = useState(null)
    const [backDoc, setBackDoc] = useState(null)

    const { forms, fetchAllData } = useContext(Context)
    const [verified, setVerified] = useState(false)

    const [modal, setModal] = useState({
        open: false,
        type: '',
        message: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        // tax_identification_number: '',
        document_number: '',
        document_issuance_date: '',
        document_expiration_date: '',
        id_type: '',
    })

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    const handleDateChange = (date, fieldName) => {
        if (!date) return;

        const formattedDate = format(date, 'yyyy-MM-dd');
        setFormData(prev => ({
            ...prev,
            [fieldName]: formattedDate
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            !formData.id_type ||
            !formData.document_number ||
            !formData.document_issuance_date ||
            !formData.document_expiration_date ||
            (formData.id_type === "PASSPORT" && !passport) ||
            (formData.id_type !== "PASSPORT" && (!frontDoc || !backDoc))
        ) {
            alert('Please fill in all required fields');
            return; // prevent navigation
        }

        setIsSubmitting(true);

        try {
            const token = localStorage.getItem('token')
            const storedForm = JSON.parse(localStorage.getItem('KYC-Data'))

            const formDatas = new FormData();

            Object.entries(storedForm).forEach(([key, value]) => {
                formDatas.append(key, value);
            });
            formDatas.append("document_issuance_date", formData.document_issuance_date);
            formDatas.append("document_expiration_date", formData.document_expiration_date);
            formDatas.append("document_number", formData.document_number);

            if (formData.id_type === 'PASSPORT') {
                formDatas.append("passport", passport);
            } else {
                formDatas.append("front_national_id", frontDoc);
                formDatas.append("back_national_id", backDoc);
            }
            formDatas.append("proof_of_address_document", forms);

            const kycRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/user/create-kyc`, {
                method: "POST",
                headers: { Authorization: `Token ${token}` },
                body: formDatas,
            });

            const kycData = await kycRes.json();

            if (!kycRes.ok) {
                // ✅ Handle backend validation errors
                if (typeof kycData === "object" && kycData !== null) {
                    const errorList = Object.entries(kycData).map(([field, messages]) => {
                        const prettifiedField = field
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase());

                        return {
                            field: prettifiedField,
                            messages: Array.isArray(messages) ? messages : [messages],
                        };
                    });

                    setModal({
                        open: true,
                        type: "error",
                        title: "Validation Errors",
                        errors: errorList, // 👈 structured error list
                    });
                } else {
                    throw new Error(kycData?.message || "KYC submission failed.");
                }
                return;
            }

            console.log("KYC Added:", kycData);

            // Wallet creation
            const walletRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/create-usdc-wallet`, {
                method: "POST",
                headers: { Authorization: `Token ${token}` },
            });
            if (!walletRes.ok) throw new Error("Wallet creation failed");

            // Customer creation
            const customerRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/user/create-customer`, {
                method: "POST",
                headers: { Authorization: `Token ${token}` },
            });
            if (!customerRes.ok) throw new Error("Customer creation failed");

            // ✅ Success modal
            setModal({
                open: true,
                type: "success",
                title: "Success",
                message: "Your KYC has been submitted successfully!",
            });

            fetchAllData();
            setVerified(!verified);
        } catch (error) {
            setModal({
                open: true,
                type: "error",
                title: "Error",
                message: error.message || "Something went wrong, please try again.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const compressImage = async (file) => {
        if (!file) return null;
        if (file.type === "application/pdf") return file; // skip pdf
        if (file.size <= 2 * 1024 * 1024) return file; // already under 2MB

        try {
            const options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
            };
            return await imageCompression(file, options);
        } catch (err) {
            console.error("Compression error:", err);
            return file;
        }
    };

    const handlePassportChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
            if (!allowedTypes.includes(file.type)) {
                alert("Only JPG, PNG, or PDF files are allowed");
                return;
            }
            const compressed = await compressImage(file);
            setPassport(compressed);
        }
    };

    const handleFrontDocChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
            if (!allowedTypes.includes(file.type)) {
                alert("Only JPG, PNG, or PDF files are allowed");
                return;
            }
            const compressed = await compressImage(file);
            setFrontDoc(compressed);
        }
    };

    const handleBackDocChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
            if (!allowedTypes.includes(file.type)) {
                alert("Only JPG, PNG, or PDF files are allowed");
                return;
            }
            const compressed = await compressImage(file);
            setBackDoc(compressed);
        }
    };

    useEffect(() => {
        const KYC_Data = JSON.parse(localStorage.getItem('KYC-Data'))
        setFormData(KYC_Data)
        console.log(forms);
    }, [forms])

    const IdOption = [
        { value: '', label: 'Select ID Type' },
        { value: 'PASSPORT', label: 'Passport' },
        { value: 'NATIONAL_ID', label: 'National ID Card' },
        { value: 'DRIVERS_LICENSE', label: `Driver's License` }
    ]

    const handleSelectID = (Option) => {
        setIdType(Option)

        const updatedFormData = {
            ...formData,
            id_type: Option.value
        };
        setFormData(updatedFormData);

        const prevData = JSON.parse(localStorage.getItem('KYC-Data')) || {};
        const mergedData = {
            ...prevData,
            ...updatedFormData
        };

        localStorage.setItem('KYC-Data', JSON.stringify(mergedData));
    }

    useEffect(() => {
        const KYC_Data = JSON.parse(localStorage.getItem('KYC-Data'))
        if (KYC_Data) setFormData(KYC_Data)
    }, [])

    const customStyles = {
        indicatorSeparator: () => ({
            display: 'none'
        }),
        control: (base) => ({
            ...base,
            borderColor: '#F3F0F7',
            padding: '4px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px 0 #1D1C1F0D',
            borderWidth: '1.2px'
        }),
        singleValue: (base) => ({
            ...base,
            color: '#78757A',
        }),
        menu: (base) => ({
            ...base,
            zIndex: 999,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? '#F3F0F7' : 'white',
            color: 'black',
            padding: 10,
            cursor: 'pointer',
        }),
    }

    return (
        <form onSubmit={handleSubmit} className="flex flex-col max-w-[560px] mx-auto md:p-10 py-10 px-4">
            <div>
                {/* <h1 className='text-[28px] font-medium text-[#1D1C1F] text-center'>Verify Your Identity</h1>
                <p className='text-[#525154] text-[14px] font-normal text-center'>
                    Reccur is required by law and/or industry regulation to collect this information.
                </p> */}
                <h1 className='text-[28px] font-medium text-[#1D1C1F] text-center'>Upload your Documents</h1>
                <p className='text-[#525154] text-[14px] font-normal  text-center'>
                    Help us verify your identity by uploading the required documents. This ensures your
                    account stays secure and fully compliant.
                </p>
            </div>
            <div className='flex items-center justify-center gap-2 mt-5 transition-all duration-300'>
                <p className={location.pathname === '/dashboard/verifyaddress' ? 'bg-[#411c87] h-1 w-20 rounded-md' : 'bg-gray-300 h-1 w-5 rounded-md'}></p>
                {/* <p className={location.pathname === '/dashboard/verifyidentity' ? 'bg-[#411c87] h-1 w-20 rounded-md' : 'bg-gray-300 h-1 w-5 rounded-md'}></p> */}
                <p className={location.pathname === '/dashboard/uploadDocument' ? 'bg-[#411c87] h-1 w-20 rounded-md' : 'bg-gray-300 h-1 w-5 rounded-md'}></p>
            </div>
            <div className='mt-12 flex flex-col gap-6 text-[#525154] font-medium text-[14px]'>
                <div>
                    <label htmlFor="code">Select Document <span className='text-red-400'>*</span></label>
                    <Select
                        styles={customStyles}
                        options={IdOption}
                        value={idType}
                        onChange={handleSelectID}
                        isSearchable={false}
                        className='rounded-m w-full mt-1 outline-none'
                    />
                </div>

                <div>
                    <label htmlFor="code">Document Number <span className='text-red-400'>*</span></label>
                    <input
                        className='border-[1.2px] mt-1 border-black/20 rounded-md w-full py-[10px] px-[14px] outline-none'
                        type="text"
                        name="document_number"
                        value={formData.document_number}
                        onChange={handleChange}
                        id=""

                        placeholder='Enter your document number'
                    />
                </div>
                <div className='flex flex-col'>
                    <label htmlFor="document_expiration_date">Document issuance date <span className='text-red-400'>*</span></label>
                    <DatePicker
                        selected={
                            formData.document_issuance_date
                                ? new Date(formData.document_issuance_date.replace(/\//g, '-'))
                                : null
                        }
                        onChange={(date) => handleDateChange(date, 'document_issuance_date')}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="YYYY-MM-DD"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                        showMonthDropdown
                        dropdownMode="select"
                        customInput={<CustomInput />}
                        popperPlacement="bottom"
                    />
                </div>
                <div className='flex flex-col'>
                    <label htmlFor="document_expiration_date">Document expiration date <span className='text-red-400'>*</span></label>
                    <DatePicker
                        selected={
                            formData.document_expiration_date
                                ? new Date(formData.document_expiration_date.replace(/\//g, '-'))
                                : null
                        }
                        onChange={(date) => handleDateChange(date, 'document_expiration_date')}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="YYYY-MM-DD"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                        showMonthDropdown
                        dropdownMode="select"
                        customInput={<CustomInput />}
                        popperPlacement="bottom"
                    />
                </div>
                {formData.id_type === 'PASSPORT' ? (
                    <div className='w-full'>
                        <label htmlFor="document_expiration_date">Upload Document <span className='text-red-400'>*</span></label>
                        <label htmlFor="passport">
                            <div className='bg-[#eae2f6] cursor-pointer rounded-md h-[150px] border-2 border-black/50 flex items-center justify-center'>
                                {passport ? (
                                    <img
                                        src={URL.createObjectURL(passport)}
                                        alt=''
                                        className='w-full h-[140px] rounded-md cursor-pointer object-cover'
                                    />
                                ) : (
                                    <FaRegImage className='text-[25px] text-black/40' />
                                )}
                            </div>
                            <input
                                type="file"
                                name=""
                                id="passport"
                                onChange={handlePassportChange}
                                hidden
                            />
                        </label>
                        <p className='text-[#531CB3]'>Upload Passport</p>
                    </div>
                ) : (
                    <>
                        <div>
                            {/* <span className='text-black/50'>(File must be less than 2mb)</span> */}
                            <label htmlFor="document_expiration_date">Upload Document <span className='text-red-400'>*</span></label>
                            <div className='flex gap-4 mt-1'>
                                <div className='w-full'>
                                    <label htmlFor="frontDoc">
                                        <div className='bg-[#eae2f6] cursor-pointer rounded-md h-[150px] border-2 border-black/50 flex items-center justify-center'>
                                            {frontDoc ? (
                                                <img
                                                    src={URL.createObjectURL(frontDoc)}
                                                    alt=''
                                                    className='w-full h-[140px] rounded-md cursor-pointer object-cover'
                                                />
                                            ) : (
                                                <FaRegImage className='text-[25px] text-black/40' />
                                            )}
                                        </div>
                                        <input
                                            type="file"
                                            name=""
                                            id="frontDoc"
                                            onChange={handleFrontDocChange}
                                            hidden
                                        />
                                    </label>
                                    <p className='text-[#531CB3]'>Upload Front</p>
                                </div>
                                <div className='w-full'>
                                    <label htmlFor="backDoc">
                                        <div className='bg-[#eae2f6] cursor-pointer rounded-md h-[150px] border-2 border-black/50 flex items-center justify-center'>
                                            {backDoc ? (
                                                <img
                                                    src={URL.createObjectURL(backDoc)}
                                                    alt=''
                                                    className='w-full h-[140px] rounded-md cursor-pointer object-cover'
                                                />
                                            ) : (
                                                <FaRegImage className='text-[25px] text-black/40' />
                                            )}
                                        </div>
                                        <input
                                            type="file"
                                            name=""
                                            id="backDoc"
                                            onChange={handleBackDocChange}
                                            hidden
                                        />
                                    </label>
                                    <p className='text-[#531CB3]'>Upload Back</p>
                                </div>
                            </div>
                        </div>
                        <p className='text-black/50 mt-[-10px]'>(NIN slip not allowed)</p>
                    </>
                )}
                <div className='flex gap-2 mt-4'>
                    <p
                        className='p-3 rounded-lg w-[25%] cursor-pointer text-center border-[1.2px] border-black/10'
                        onClick={() => {
                            navigate('/dashboard/verifyidentity')
                            window.scrollTo(0, 0)
                        }}
                    >
                        Previous
                    </p>
                    <button
                        type="submit"
                        className={`p-[10px] px-4 rounded-lg text-white w-[80%] ${isSubmitting ? 'bg-[#E8E1F5]' : 'bg-[#531CB3]'}`}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Loading...' : 'Continue'}
                    </button>
                </div>

                <AnimatePresence>
                    {modal.open && (
                        <motion.div
                            className="fixed inset-0 flex items-center justify-center bg-black/50 z-50"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full text-center"
                                initial={{ opacity: 0, scale: 0.8, y: 50 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.8, y: 50 }}
                                transition={{ duration: 0.3, ease: "easeOut" }}
                            >
                                {/* Title */}
                                <h2
                                    className={`text-lg font-semibold ${modal.type === "success" ? "text-green-600" : "text-red-600"
                                        }`}
                                >
                                    {modal.title || (modal.type === "success" ? "Success" : "Error")}
                                </h2>

                                {/* ✅ Errors List or Single Message */}
                                {modal.errors ? (
                                    <ul className="mt-3 text-left text-red-600 list-disc list-inside space-y-1">
                                        {modal.errors.map((err, i) => (
                                            <li key={i}>
                                                <strong>{err.field}:</strong> {err.messages.join(", ")}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="mt-2 text-gray-600">{modal.message}</p>
                                )}

                                {/* Button */}
                                <button
                                    className="mt-4 px-4 py-2 bg-[#531CB3] text-white rounded-lg hover:bg-[#411c87] transition"
                                    onClick={() => {
                                        setModal({ ...modal, open: false });
                                        if (modal.type === "success") navigate("/home");
                                    }}
                                >
                                    {modal.type === "success" ? "Continue" : "Close"}
                                </button>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </form>
    )
}

export default VerifyIdentity